/* ============================================================================
   Product navigator (custom) — multi-category
   ============================================================================ */
(function () {
  'use strict';

  var ANIM_DURATION = 300;
  var SECTION_ID_FOR_FETCH = 'main-collection-product-grid';

  function initContainer(container) {
    if (container.__pfInitialized) return;
    container.__pfInitialized = true;

    var sectionId = container.getAttribute('data-pf-section-id') || '';
    var showGrid = container.getAttribute('data-pf-show-grid') === 'true';
    var defaultCatHandle = container.getAttribute('data-pf-default-cat') || '';

    var modal = container.closest('flexible-modal');
    var modalId = modal ? modal.getAttribute('id') : '';

    var categoryPickerPanel = container.querySelector('[data-panel-id="category-picker"]');

    var state = {
      activeCategory: null,       // category handle, or null when on picker
      activeCategoryBaseUrl: '',  // base collection URL for active category
      filters: Object.create(null),
      stack: [categoryPickerPanel],
      isAnimating: false
    };

    /* ------------------------------------------------------------------------
       Utilities
       ------------------------------------------------------------------------ */
    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function stateHash() {
      var keys = Object.keys(state.filters).sort();
      return state.activeCategory + ':' + keys.map(function (k) {
        return k + '=' + state.filters[k];
      }).join('&');
    }

    function facetsCacheKey(catHandle, snapshot) {
      var keys = Object.keys(snapshot).sort();
      return catHandle + '|' + keys.map(function (k) { return k + '=' + snapshot[k]; }).join('&');
    }

    function getStepPanelsForCategory(catHandle) {
      return container.querySelectorAll('.pf-panel[data-pf-cat-handle="' + catHandle + '"][data-pf-step-index]');
    }

    function getFirstStepPanel(catHandle) {
      return container.querySelector('.pf-panel[data-pf-cat-handle="' + catHandle + '"][data-pf-step-index="1"]');
    }

    function getResultPanel(catHandle) {
      return container.querySelector('[data-panel-id="result-' + catHandle + '"]');
    }

    /* ------------------------------------------------------------------------
       URL building (per category)
       ------------------------------------------------------------------------ */
    function buildFilterParams(filters) {
      var params = new URLSearchParams();
      Object.keys(filters || {}).forEach(function (key) {
        var val = filters[key];
        if (val != null && val !== '') {
          params.append(key, val);
        }
      });
      params.append('filter.v.availability', '1');
      return params;
    }

    function buildUrlForState(baseUrl, filters) {
      var qs = buildFilterParams(filters).toString();
      return qs ? baseUrl + '?' + qs : baseUrl;
    }

    /* ------------------------------------------------------------------------
       Facets cache + fetch
       ------------------------------------------------------------------------ */
    var facetsCache = Object.create(null);
    var inflightFacets = Object.create(null);

    function fetchFilterMapForState(baseUrl, catHandle, filtersSnapshot) {
      var key = facetsCacheKey(catHandle, filtersSnapshot);
      if (facetsCache[key]) return Promise.resolve(facetsCache[key]);
      if (inflightFacets[key]) return inflightFacets[key];

      var params = buildFilterParams(filtersSnapshot);
      params.append('section_id', SECTION_ID_FOR_FETCH);
      var url = baseUrl + '?' + params.toString();

      var p = fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var map = parseFiltersFromDoc(doc);
          facetsCache[key] = map;
          delete inflightFacets[key];
          return map;
        })
        .catch(function (err) {
          delete inflightFacets[key];
          throw err;
        });

      inflightFacets[key] = p;
      return p;
    }

    function parseFiltersFromDoc(doc) {
      var map = Object.create(null);
      var seen = Object.create(null); // dedup key: param|value
      var inputs = doc.querySelectorAll('input[name^="filter.p"]');
      Array.prototype.forEach.call(inputs, function (input) {
        if (input.type === 'hidden') return;
        if (input.hasAttribute('disabled')) return;
        var paramName = input.getAttribute('name');
        var value = input.getAttribute('value');
        if (!paramName || !value) return;

        // Hyper's facets snippet renders the same filter set twice (sidebar +
        // mobile/popup variant). Dedup by param + value.
        var dedupKey = paramName + '|' + value;
        if (seen[dedupKey]) return;
        seen[dedupKey] = true;

        if (!map[paramName]) map[paramName] = [];

        // Hyper structure: <li> > <input> + <label for="input-id">
        //   <label> contains: <span class="reversed-link__text">LABEL</span>
        //                     <span class="count">N</span>
        var labelEl = null;
        var inputId = input.getAttribute('id');
        if (inputId) {
          labelEl = doc.querySelector('label[for="' + inputId.replace(/"/g, '\\"') + '"]');
        }
        if (!labelEl) {
          var li = input.closest('li') || input.parentElement;
          if (li) labelEl = li.querySelector('label') || li;
        }

        var label = value;
        var count = 0;
        if (labelEl) {
          var labelTextEl =
            labelEl.querySelector('.reversed-link__text') ||
            labelEl.querySelector('.label, .facet__label, .facet-checkbox__text');
          if (labelTextEl) {
            label = (labelTextEl.textContent || '').trim();
          } else {
            label = (labelEl.textContent || '').trim();
          }

          var countEl = labelEl.querySelector('.count');
          if (countEl) {
            var n = parseInt((countEl.textContent || '').replace(/[^\d]/g, ''), 10);
            if (!isNaN(n)) count = n;
          } else {
            var countText = (labelEl.textContent || '').match(/\((\d+)\)/);
            if (countText) count = parseInt(countText[1], 10);
          }
        }

        label = label.replace(/\s*\(\d+\)\s*$/, '').trim();

        map[paramName].push({ value: value, label: label, count: count });
      });
      return map;
    }

    /* ------------------------------------------------------------------------
       Grid mode (preview/result content)
       ------------------------------------------------------------------------ */
    var gridModeCache = Object.create(null);
    var inflightGridMode = Object.create(null);

    function gridModeUrlForCache(url, catHandle) {
      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      var targetSection = sectionId || 'product-navigator-custom';
      return url + sep + 'section_id=' + encodeURIComponent(targetSection) +
        '&pfgridmode=1&pfcat=' + encodeURIComponent(catHandle);
    }

    function fetchGridMode(url, catHandle) {
      var cacheUrl = gridModeUrlForCache(url, catHandle);
      if (gridModeCache[cacheUrl]) return Promise.resolve(gridModeCache[cacheUrl]);
      if (inflightGridMode[cacheUrl]) return inflightGridMode[cacheUrl];

      var sep = url.indexOf('?') >= 0 ? '&' : '?';
      var GRID_QS = 'pfgridmode=1&pfcat=' + encodeURIComponent(catHandle);
      var sid = sectionId || 'product-navigator-custom';
      var fetchUrl = url + sep + 'section_id=' + encodeURIComponent(sid) + '&' + GRID_QS;

      var p = fetch(fetchUrl, { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) {
          if (!html) return null;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (doc.querySelector('[data-pf-grid-root]') ||
              doc.querySelector('#ProductsList-PFG') ||
              doc.querySelector('[data-pf-grid-count]')) {
            return doc;
          }
          return null;
        })
        .catch(function () { return null; })
        .then(function (doc) {
          if (doc) gridModeCache[cacheUrl] = doc;
          delete inflightGridMode[cacheUrl];
          return doc;
        });

      inflightGridMode[cacheUrl] = p;
      return p;
    }

    function extractCount(doc) {
      if (!doc) return 0;
      var el = doc.querySelector('[data-pf-grid-count]');
      if (el) {
        var n = parseInt(el.getAttribute('data-pf-grid-count'), 10);
        if (!isNaN(n)) return n;
      }
      return 0;
    }

    /* ------------------------------------------------------------------------
       Option rendering
       ------------------------------------------------------------------------ */
    function showOptionsSkeleton(optionsEl) {
      var html = '<div class="pf-options__skeleton" data-pf-skeleton aria-hidden="true">';
      for (var i = 0; i < 6; i++) html += '<div class="pf-option-skeleton"></div>';
      html += '</div>';
      optionsEl.innerHTML = html;
    }

    function populatePanelOptions(panel, values) {
      var optionsEl = panel.querySelector('[data-pf-options]');
      if (!optionsEl) return;

      var nextTarget = panel.getAttribute('data-pf-next-target');

      var html = '';
      if (values && values.length) {
        values.forEach(function (v) {
          if (!v || v.count === 0) return;
          html +=
            '<button class="pf-option flex items-center justify-between" type="button" data-pf-option ' +
            'data-target="' + escapeHtml(nextTarget) + '" ' +
            'data-pf-value="' + escapeHtml(v.value) + '">' +
            '<span class="pf-option__label h5">' + escapeHtml(v.label) + '</span>' +
            '<span class="pf-option__count font-body text-subtext">' + v.count + '</span>' +
            '</button>';
        });
      }

      if (!html) {
        html = '<p class="pf-options__empty">該当する選択肢がありません</p>';
      }

      if (panel.hasAttribute('data-pf-allow-skip')) {
        var skipLabel = panel.getAttribute('data-pf-skip-label') || 'スキップ';
        html +=
          '<button class="pf-option pf-option--skip flex justify-start text-subtext" type="button" data-pf-option data-pf-skip ' +
          'data-target="' + escapeHtml(nextTarget) + '">' +
          '<span class="pf-option__label h5">' + escapeHtml(skipLabel) + '</span>' +
          '</button>';
      }

      optionsEl.innerHTML = html;
    }

    /* ------------------------------------------------------------------------
       Refresh panel options + step preview
       ------------------------------------------------------------------------ */
    var panelFetchTokens = new WeakMap();
    var previewFetchTokens = new WeakMap();

    function prefetchForOption(currentPanel, optionEl) {
      if (!currentPanel || !optionEl) return;
      if (optionEl.__pfPrefetched) return; // dedup
      optionEl.__pfPrefetched = true;

      var catHandle = currentPanel.getAttribute('data-pf-cat-handle');
      var baseUrl = currentPanel.getAttribute('data-pf-base-url');
      var currentParam = currentPanel.getAttribute('data-pf-filter-param');
      var value = optionEl.getAttribute('data-pf-value');
      if (!catHandle || !baseUrl) return;

      var snapshot = {};
      Object.keys(state.filters).forEach(function (k) { snapshot[k] = state.filters[k]; });
      if (currentParam && value) snapshot[currentParam] = value;

      var nextTarget = currentPanel.getAttribute('data-pf-next-target');
      var nextPanel = nextTarget && nextTarget.indexOf('result-') !== 0
        ? container.querySelector('[data-panel-id="' + nextTarget + '"]')
        : null;

      // Prefetch facets for next step (if next step is a filter step)
      if (nextPanel && nextPanel.hasAttribute('data-pf-filter-param')) {
        fetchFilterMapForState(baseUrl, catHandle, snapshot).catch(function () {});
      }
      // Prefetch grid for next step preview
      var nextUrl = buildUrlForState(baseUrl, snapshot);
      fetchGridMode(nextUrl, catHandle).catch(function () {});
    }

    function prefetchNextStep(currentPanel) {
      if (!currentPanel) return;
      // Prefetch for the first 3 non-skip options (cover common user choices)
      var opts = currentPanel.querySelectorAll('[data-pf-option]:not([data-pf-skip])[data-pf-value]');
      var limit = Math.min(opts.length, 3);
      for (var i = 0; i < limit; i++) {
        prefetchForOption(currentPanel, opts[i]);
      }
    }

    function bindHoverPrefetch(panel) {
      if (!panel || panel.__pfHoverBound) return;
      panel.__pfHoverBound = true;

      var optsEl = panel.querySelector('[data-pf-options]');
      if (!optsEl) return;

      // Delegated mouseover/touchstart prefetch
      var prefetchOnInteract = function (e) {
        var opt = e.target.closest('[data-pf-option]:not([data-pf-skip])[data-pf-value]');
        if (!opt || !panel.contains(opt)) return;
        prefetchForOption(panel, opt);
      };
      optsEl.addEventListener('mouseover', prefetchOnInteract, { passive: true });
      optsEl.addEventListener('touchstart', prefetchOnInteract, { passive: true });
    }

    function refreshPanelOptionsIfNeeded(panel) {
      if (!panel) return Promise.resolve();
      var filterParam = panel.getAttribute('data-pf-filter-param');
      if (!filterParam) return Promise.resolve();

      var catHandle = panel.getAttribute('data-pf-cat-handle');
      var baseUrl = panel.getAttribute('data-pf-base-url');
      if (!catHandle || !baseUrl) return Promise.resolve();

      var currentHash = stateHash();
      var storedHash = panel.getAttribute('data-pf-populated-hash');

      if (storedHash !== null && storedHash === currentHash && !panel.hasAttribute('data-pf-needs-fetch')) {
        return Promise.resolve();
      }

      var optionsEl = panel.querySelector('[data-pf-options]');
      if (!optionsEl) return Promise.resolve();

      var snapshot = {};
      Object.keys(state.filters).forEach(function (k) { snapshot[k] = state.filters[k]; });
      var snapshotHash = currentHash;

      // Cache hit fast path
      var cacheKey = facetsCacheKey(catHandle, snapshot);
      if (facetsCache[cacheKey]) {
        var cachedValues = facetsCache[cacheKey][filterParam] || [];
        populatePanelOptions(panel, cachedValues);
        panel.setAttribute('data-pf-populated-hash', snapshotHash);
        panel.removeAttribute('data-pf-needs-fetch');
        panel.classList.remove('pf-panel--options-loading');
        prefetchNextStep(panel);
        bindHoverPrefetch(panel);
        return Promise.resolve();
      }

      var token = {};
      panelFetchTokens.set(panel, token);

      showOptionsSkeleton(optionsEl);
      panel.classList.add('pf-panel--options-loading');

      return fetchFilterMapForState(baseUrl, catHandle, snapshot)
        .then(function (map) {
          if (panelFetchTokens.get(panel) !== token) return;
          var values = map[filterParam] || [];
          populatePanelOptions(panel, values);
          panel.setAttribute('data-pf-populated-hash', snapshotHash);
          panel.removeAttribute('data-pf-needs-fetch');
          panel.classList.remove('pf-panel--options-loading');
          prefetchNextStep(panel);
          bindHoverPrefetch(panel);
        })
        .catch(function (err) {
          if (panelFetchTokens.get(panel) !== token) return;
          console.error('[pf] options fetch failed:', err);
          optionsEl.innerHTML = '<p class="pf-options__empty">読み込みに失敗しました。再度お試しください。</p>';
          panel.classList.remove('pf-panel--options-loading');
        });
    }

    function refreshStepPreviewIfNeeded(panel) {
      if (!panel) return Promise.resolve();
      var previewEl = panel.querySelector('[data-pf-step-preview]');
      if (!previewEl) return Promise.resolve();

      var catHandle = panel.getAttribute('data-pf-cat-handle');
      var baseUrl = panel.getAttribute('data-pf-base-url');
      if (!catHandle || !baseUrl) return Promise.resolve();

      var currentHash = stateHash();
      var storedHash = previewEl.getAttribute('data-pf-preview-hash');
      if (storedHash !== null && storedHash === currentHash) return Promise.resolve();

      var snapshot = {};
      Object.keys(state.filters).forEach(function (k) { snapshot[k] = state.filters[k]; });
      var snapshotHash = currentHash;

      var loadingEl = previewEl.querySelector('[data-pf-preview-loading]');
      var gridEl = previewEl.querySelector('[data-pf-preview-grid]');
      var emptyEl = previewEl.querySelector('[data-pf-preview-empty]');
      var countEl = previewEl.querySelector('[data-pf-preview-count]');
      var countCtaEl = previewEl.querySelector('[data-pf-preview-count-cta]');
      var ctaEl = previewEl.querySelector('[data-pf-preview-cta]');

      var url = buildUrlForState(baseUrl, snapshot);
      if (ctaEl) ctaEl.setAttribute('href', url);

      function applyGridDoc(doc) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (!doc) {
          if (emptyEl) emptyEl.hidden = false;
          if (countEl) countEl.textContent = '?';
          if (countCtaEl) countCtaEl.textContent = '?';
          return;
        }
        var count = extractCount(doc);
        if (countEl) countEl.textContent = count;
        if (countCtaEl) countCtaEl.textContent = count;
        var src = doc.querySelector('[data-pf-grid-root]') || doc.querySelector('#ProductsList-PFG');
        if (!src || src.children.length === 0 || count === 0) {
          if (emptyEl) emptyEl.hidden = false;
        } else if (gridEl) {
          var clone = src.cloneNode(true);
          var motionEls = clone.querySelectorAll('[data-motion], [data-motion-delay]');
          Array.prototype.forEach.call(motionEls, function (el) {
            el.removeAttribute('data-motion');
            el.removeAttribute('data-motion-delay');
          });
          gridEl.innerHTML = '';
          gridEl.appendChild(clone);
          gridEl.hidden = false;
        }
      }

      var cachedDoc = gridModeCache[gridModeUrlForCache(url, catHandle)];
      if (cachedDoc) {
        applyGridDoc(cachedDoc);
        previewEl.setAttribute('data-pf-preview-hash', snapshotHash);
        return Promise.resolve();
      }

      var token = {};
      previewFetchTokens.set(previewEl, token);

      if (loadingEl) loadingEl.style.display = '';
      if (gridEl) { gridEl.innerHTML = ''; gridEl.hidden = true; }
      if (emptyEl) emptyEl.hidden = true;

      return fetchGridMode(url, catHandle)
        .then(function (doc) {
          if (previewFetchTokens.get(previewEl) !== token) return;
          applyGridDoc(doc);
          previewEl.setAttribute('data-pf-preview-hash', snapshotHash);
        })
        .catch(function (err) {
          if (previewFetchTokens.get(previewEl) !== token) return;
          console.error('[pf] preview fetch failed:', err);
          if (loadingEl) loadingEl.style.display = 'none';
          if (emptyEl) emptyEl.hidden = false;
        });
    }

    /* ------------------------------------------------------------------------
       Result panel load (similar to step preview, but on dedicated result panel)
       ------------------------------------------------------------------------ */
    function loadResult() {
      var resultPanel = state.stack[state.stack.length - 1];
      if (!resultPanel) return;

      var catHandle = resultPanel.getAttribute('data-pf-cat-handle');
      var baseUrl = resultPanel.getAttribute('data-pf-base-url');
      if (!catHandle || !baseUrl) return;

      var loadingEl = resultPanel.querySelector('[data-pf-grid-loading]');
      var gridEl = resultPanel.querySelector('[data-pf-result-grid]');
      var countEl = resultPanel.querySelector('[data-pf-result-count]');
      var countCtaEl = resultPanel.querySelector('[data-pf-result-count-cta]');
      var ctaEl = resultPanel.querySelector('[data-pf-result-cta]');

      var snapshot = {};
      Object.keys(state.filters).forEach(function (k) { snapshot[k] = state.filters[k]; });

      var url = buildUrlForState(baseUrl, snapshot);
      if (ctaEl) ctaEl.setAttribute('href', url);

      if (loadingEl) loadingEl.style.display = '';
      if (gridEl) { gridEl.innerHTML = ''; gridEl.hidden = true; }

      fetchGridMode(url, catHandle).then(function (doc) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (!doc) {
          if (countEl) countEl.textContent = '?';
          if (countCtaEl) countCtaEl.textContent = '?';
          return;
        }
        var count = extractCount(doc);
        if (countEl) countEl.textContent = count;
        if (countCtaEl) countCtaEl.textContent = count;

        var src = doc.querySelector('[data-pf-grid-root]') || doc.querySelector('#ProductsList-PFG');
        if (src && src.children.length > 0 && gridEl && showGrid) {
          var clone = src.cloneNode(true);
          var motionEls = clone.querySelectorAll('[data-motion], [data-motion-delay]');
          Array.prototype.forEach.call(motionEls, function (el) {
            el.removeAttribute('data-motion');
            el.removeAttribute('data-motion-delay');
          });
          gridEl.innerHTML = '';
          gridEl.appendChild(clone);
          gridEl.hidden = false;
        }
      });
    }

    /* ------------------------------------------------------------------------
       Selected chips
       ------------------------------------------------------------------------ */
    function renderSelectedChips(panel) {
      if (!panel) return;
      var chipsEl = panel.querySelector('[data-pf-selected-chips]');
      if (!chipsEl) return;

      var currentStepIndex = parseInt(panel.getAttribute('data-pf-step-index'), 10);
      // For result panel, currentStepIndex is NaN. Treat result panel as
      // "after all steps" so all step chips display, plus category chip.
      var isResult = panel.classList.contains('pf-panel--result');
      if (isNaN(currentStepIndex)) currentStepIndex = Infinity;

      var catHandle = panel.getAttribute('data-pf-cat-handle');
      if (!catHandle) {
        chipsEl.innerHTML = '';
        return;
      }

      var catLabel = panel.getAttribute('data-pf-cat-label') || catHandle;

      var html = '';

      // Category chip (always first, clicking returns to category picker)
      html +=
        '<a href="#" class="pf-chip pf-chip--category btn btn--secondary flex relative" ' +
        'data-pf-chip data-pf-chip-category ' +
        'role="button" aria-label="カテゴリを変更">' +
          '<div class="btn__text flex items-center justify-between gap-2">' +
            '<div class="facet-title text-wrap flex items-center gap-2">' +
              escapeHtml(catLabel) +
            '</div>' +
            '<svg class="icon icon--extra-small" width="10" height="10" viewBox="0 0 10 10" ' +
              'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">' +
              '<line x1="1" y1="1" x2="9" y2="9"></line>' +
              '<line x1="9" y1="1" x2="1" y2="9"></line>' +
            '</svg>' +
          '</div>' +
        '</a>';

      // Step chips (each previous step's chosen value)
      var allStepPanels = getStepPanelsForCategory(catHandle);
      Array.prototype.forEach.call(allStepPanels, function (p) {
        var idx = parseInt(p.getAttribute('data-pf-step-index'), 10);
        if (isNaN(idx) || idx >= currentStepIndex) return;

        var param = p.getAttribute('data-pf-filter-param');
        var value = param ? state.filters[param] : null;
        if (!value) return;

        html +=
          '<a href="#" class="pf-chip btn btn--secondary flex relative" ' +
          'data-pf-chip data-pf-chip-step="' + idx + '" ' +
          'role="button" aria-label="Step ' + idx + ' を編集">' +
            '<div class="btn__text flex items-center justify-between gap-2">' +
              '<div class="facet-title text-wrap flex items-center gap-2">' +
                escapeHtml(value) +
              '</div>' +
              '<svg class="icon icon--extra-small" width="10" height="10" viewBox="0 0 10 10" ' +
                'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">' +
                '<line x1="1" y1="1" x2="9" y2="9"></line>' +
                '<line x1="9" y1="1" x2="1" y2="9"></line>' +
              '</svg>' +
            '</div>' +
          '</a>';
      });

      chipsEl.innerHTML = html;
    }

    /* ------------------------------------------------------------------------
       Progress + UI updates
       ------------------------------------------------------------------------ */
    function updateUI() {
      var top = state.stack[state.stack.length - 1];
      if (!top) return;

      var backBtn = container.querySelector('[data-pf-back]');
      if (backBtn) {
        if (state.stack.length > 1) backBtn.classList.remove('is-invisible');
        else backBtn.classList.add('is-invisible');
      }

      // Hide modal heading on result panel (proposal screen)
      var headingEl = container.querySelector('.pf-header__heading');
      if (headingEl) {
        if (top.classList.contains('pf-panel--result')) {
          headingEl.classList.add('is-invisible');
        } else {
          headingEl.classList.remove('is-invisible');
        }
      }

      var progressFill = container.querySelector('[data-pf-progress-fill]');
      var progressText = container.querySelector('[data-pf-progress-text]');
      var stepIdx = parseInt(top.getAttribute('data-pf-step-index'), 10);
      var stepCount = parseInt(top.getAttribute('data-pf-step-count'), 10);

      // If the flow started from the category picker (rather than a direct
      // category alias), treat the category picker itself as "Step 1" so that
      // numbered steps shift to 2, 3, ...
      var startedFromPicker = state.stack[0] === categoryPickerPanel;
      var pickerOffset = startedFromPicker ? 1 : 0;

      if (top === categoryPickerPanel) {
        // Category picker is active
        if (startedFromPicker) {
          // Show "1 / (stepCount + 1)" — but we don't know which category yet,
          // so just show "1 / ?" using the longest category step count if any.
          // Simplest: count any single category's steps and add 1 for picker.
          var anyCatStepCount = 0;
          var allStepPanels = container.querySelectorAll('.pf-panel[data-pf-step-count]');
          Array.prototype.forEach.call(allStepPanels, function (p) {
            var c = parseInt(p.getAttribute('data-pf-step-count'), 10);
            if (!isNaN(c) && c > anyCatStepCount) anyCatStepCount = c;
          });
          var totalWithPicker = anyCatStepCount + 1;
          if (progressFill) progressFill.style.width = (1 / (totalWithPicker + 1) * 100) + '%';
          if (progressText) {
            progressText.textContent = '1 / ' + totalWithPicker;
            progressText.classList.remove('is-invisible');
          }
        } else {
          if (progressFill) progressFill.style.width = '0%';
          if (progressText) progressText.classList.add('is-invisible');
        }
      } else if (!isNaN(stepIdx) && !isNaN(stepCount) && stepCount > 0) {
        // Step panel
        var displayIdx = stepIdx + pickerOffset;
        var displayCount = stepCount + pickerOffset;
        var pct = (displayIdx / (displayCount + 1)) * 100;
        if (progressFill) progressFill.style.width = pct + '%';
        if (progressText) {
          progressText.textContent = displayIdx + ' / ' + displayCount;
          progressText.classList.remove('is-invisible');
        }
      } else if (top.classList.contains('pf-panel--result')) {
        if (progressFill) progressFill.style.width = '100%';
        if (progressText) progressText.classList.add('is-invisible');
      } else {
        if (progressFill) progressFill.style.width = '0%';
        if (progressText) progressText.classList.add('is-invisible');
      }
    }

    /* ------------------------------------------------------------------------
       Slide animations
       ------------------------------------------------------------------------ */
    function slideForward(currentPanel, targetId) {
      var targetPanel = container.querySelector('[data-panel-id="' + targetId + '"]');
      if (!targetPanel) return;

      if (targetPanel.hasAttribute('data-pf-step-index')) {
        refreshPanelOptionsIfNeeded(targetPanel);
        refreshStepPreviewIfNeeded(targetPanel);
        // Bind hover prefetch on the target panel (idempotent)
        bindHoverPrefetch(targetPanel);
      }
      renderSelectedChips(targetPanel);

      state.isAnimating = true;
      currentPanel.classList.remove('is-active');
      currentPanel.classList.add('is-exit-fwd');
      targetPanel.classList.add('is-enter-fwd');

      window.setTimeout(function () {
        currentPanel.classList.remove('is-exit-fwd');
        targetPanel.classList.remove('is-enter-fwd');
        targetPanel.classList.add('is-active');
        state.stack.push(targetPanel);
        state.isAnimating = false;
        updateUI();

        if (targetPanel.classList.contains('pf-panel--result')) {
          loadResult();
        }
      }, ANIM_DURATION);
    }

    function slideBack() {
      if (state.stack.length <= 1) return;
      state.isAnimating = true;

      var current = state.stack.pop();
      var prev = state.stack[state.stack.length - 1];

      // If going back to category picker, clear all state
      if (prev === categoryPickerPanel) {
        state.filters = Object.create(null);
        state.activeCategory = null;
        state.activeCategoryBaseUrl = '';
      } else {
        // Clear filters for the panel we're going back TO and any later steps in this category
        var prevIndex = parseInt(prev.getAttribute('data-pf-step-index'), 10);
        var catHandle = prev.getAttribute('data-pf-cat-handle');
        if (catHandle) {
          var stepPanels = getStepPanelsForCategory(catHandle);
          Array.prototype.forEach.call(stepPanels, function (p) {
            var idx = parseInt(p.getAttribute('data-pf-step-index'), 10);
            if (!isNaN(prevIndex) && !isNaN(idx) && idx >= prevIndex) {
              var param = p.getAttribute('data-pf-filter-param');
              if (param) delete state.filters[param];
            }
          });
        }
      }

      renderSelectedChips(prev);

      current.classList.remove('is-active');
      current.classList.add('is-exit-back');
      prev.classList.add('is-enter-back');

      window.setTimeout(function () {
        current.classList.remove('is-exit-back');
        prev.classList.remove('is-enter-back');
        prev.classList.add('is-active');
        state.isAnimating = false;
        updateUI();

        if (prev.hasAttribute('data-pf-step-index')) {
          refreshPanelOptionsIfNeeded(prev);
          refreshStepPreviewIfNeeded(prev);
        }
      }, ANIM_DURATION);
    }

    function jumpToCategoryPicker() {
      if (state.isAnimating) return;
      if (!categoryPickerPanel) return;

      var currentPanel = state.stack[state.stack.length - 1];
      if (!currentPanel || currentPanel === categoryPickerPanel) return;

      // Clear all state — leaving category means resetting selections
      state.filters = Object.create(null);
      state.activeCategory = null;
      state.activeCategoryBaseUrl = '';
      state.stack = [categoryPickerPanel];

      state.isAnimating = true;
      currentPanel.classList.remove('is-active');
      currentPanel.classList.add('is-exit-back');
      categoryPickerPanel.classList.add('is-enter-back');

      window.setTimeout(function () {
        currentPanel.classList.remove('is-exit-back');
        categoryPickerPanel.classList.remove('is-enter-back');
        categoryPickerPanel.classList.add('is-active');
        state.isAnimating = false;
        updateUI();
      }, ANIM_DURATION);
    }

    function jumpToStep(targetStepIndex) {
      if (state.isAnimating) return;
      if (isNaN(targetStepIndex)) return;

      if (!state.activeCategory) return;

      var targetPanel = container.querySelector(
        '.pf-panel[data-pf-cat-handle="' + state.activeCategory + '"]' +
        '[data-pf-step-index="' + targetStepIndex + '"]'
      );
      if (!targetPanel) return;

      var currentPanel = state.stack[state.stack.length - 1];
      if (currentPanel === targetPanel) return;

      // Clear filters for target step + later steps in this category
      var stepPanels = getStepPanelsForCategory(state.activeCategory);
      Array.prototype.forEach.call(stepPanels, function (p) {
        var idx = parseInt(p.getAttribute('data-pf-step-index'), 10);
        if (!isNaN(idx) && idx >= targetStepIndex) {
          var param = p.getAttribute('data-pf-filter-param');
          if (param) delete state.filters[param];
        }
      });

      // Trim stack to target
      while (state.stack.length > 1 && state.stack[state.stack.length - 1] !== targetPanel) {
        state.stack.pop();
      }

      state.isAnimating = true;
      currentPanel.classList.remove('is-active');
      currentPanel.classList.add('is-exit-back');
      targetPanel.classList.add('is-enter-back');

      window.setTimeout(function () {
        currentPanel.classList.remove('is-exit-back');
        targetPanel.classList.remove('is-enter-back');
        targetPanel.classList.add('is-active');
        state.isAnimating = false;
        updateUI();
        renderSelectedChips(targetPanel);
        if (targetPanel.hasAttribute('data-pf-step-index')) {
          refreshPanelOptionsIfNeeded(targetPanel);
          refreshStepPreviewIfNeeded(targetPanel);
        }
      }, ANIM_DURATION);
    }

    /* ------------------------------------------------------------------------
       Category activation
       ------------------------------------------------------------------------ */
    function activateCategory(catHandle, skipAnimation) {
      var firstStepPanel = getFirstStepPanel(catHandle);
      if (!firstStepPanel) return false;

      state.activeCategory = catHandle;
      state.activeCategoryBaseUrl = firstStepPanel.getAttribute('data-pf-base-url') || '';
      state.filters = Object.create(null);

      if (skipAnimation) {
        // Jump directly without animation (used during init)
        Array.prototype.forEach.call(container.querySelectorAll('.pf-panel.is-active'), function (p) {
          p.classList.remove('is-active');
        });
        firstStepPanel.classList.add('is-active');
        state.stack = [firstStepPanel];
        updateUI();
        renderSelectedChips(firstStepPanel);
        refreshPanelOptionsIfNeeded(firstStepPanel);
        // Hover prefetch + bulk prefetch for first-step (server-rendered case)
        bindHoverPrefetch(firstStepPanel);
        prefetchNextStep(firstStepPanel);
        return true;
      }

      slideForward(state.stack[state.stack.length - 1], firstStepPanel.getAttribute('data-panel-id'));
      return true;
    }

    /* ------------------------------------------------------------------------
       Reset (modal close)
       ------------------------------------------------------------------------ */
    function reset() {
      state.filters = Object.create(null);
      state.activeCategory = null;
      state.activeCategoryBaseUrl = '';
      state.stack = [categoryPickerPanel];
      state.isAnimating = false;

      Array.prototype.forEach.call(container.querySelectorAll('.pf-panel'), function (p) {
        p.classList.remove('is-active', 'is-enter-fwd', 'is-exit-fwd', 'is-enter-back', 'is-exit-back');
      });
      if (categoryPickerPanel) categoryPickerPanel.classList.add('is-active');

      updateUI();
    }

    /* ------------------------------------------------------------------------
       Click handler (delegated)
       ------------------------------------------------------------------------ */
    container.addEventListener('click', function (e) {
      // Category select
      var catBtn = e.target.closest('[data-pf-category-select]');
      if (catBtn) {
        if (state.isAnimating) return;
        var catHandle = catBtn.getAttribute('data-pf-cat-handle');
        if (catHandle) activateCategory(catHandle);
        return;
      }

      // Back
      var back = e.target.closest('[data-pf-back]');
      if (back) {
        if (state.isAnimating) return;
        if (back.classList.contains('is-invisible')) return;
        slideBack();
        return;
      }

      // Chip click → jump back to that step (or category picker)
      var chip = e.target.closest('[data-pf-chip]');
      if (chip) {
        if (state.isAnimating) return;
        e.preventDefault();

        // Category chip → return to category picker
        if (chip.hasAttribute('data-pf-chip-category')) {
          jumpToCategoryPicker();
          return;
        }

        var stepIdx = parseInt(chip.getAttribute('data-pf-chip-step'), 10);
        if (!isNaN(stepIdx)) jumpToStep(stepIdx);
        return;
      }

      // Restart
      var restart = e.target.closest('[data-pf-restart]');
      if (restart) {
        if (state.isAnimating) return;
        e.preventDefault();
        reset();
        return;
      }

      // Option click
      var opt = e.target.closest('[data-pf-option]');
      if (opt) {
        if (state.isAnimating) return;

        var currentPanel = state.stack[state.stack.length - 1];
        var filterParam = currentPanel.getAttribute('data-pf-filter-param');
        var value = opt.getAttribute('data-pf-value');
        var target = opt.getAttribute('data-target');
        var isSkip = opt.hasAttribute('data-pf-skip');

        if (filterParam) {
          if (isSkip || !value) {
            delete state.filters[filterParam];
          } else {
            state.filters[filterParam] = value;
          }
        }

        if (target) slideForward(currentPanel, target);
        return;
      }
    });

    /* ------------------------------------------------------------------------
       Modal open/close handling
       ------------------------------------------------------------------------ */
    function handleModalOpen(requestedCat) {
      // [カスタマイズ追記] 展開と同時にチラ防止カバーを除去 2026-06-29
      var __pfCover = document.querySelector('[data-pf-page-cover]');
      if (__pfCover) __pfCover.setAttribute('hidden', '');

      // If a category is requested via URL, jump directly to its Step 1
      var effectiveCat = requestedCat || defaultCatHandle;

      if (effectiveCat && getFirstStepPanel(effectiveCat)) {
        activateCategory(effectiveCat, true);
        renderSelectedChips(state.stack[state.stack.length - 1]);
      } else {
        // Show category picker
        reset();
      }
    }

    /* ----------------------------------------------------------------------
       [カスタマイズ追記] 診断ページ機能シェル：?cat= 自動オープン＋閉じたら直前ページへ 2026-06-29
       data-pf-return-on-close="true" は /pages/haircare-diagnosis でのみ付与される。
       ---------------------------------------------------------------------- */
    var returnOnClose = container.getAttribute('data-pf-return-on-close') === 'true';
    var __pfForwardNav = false;
    var __pfReturnTimer = null;

    // [カスタマイズ追記] 背景シェルを覆う白カバーを(再)表示。失敗時フェイドを無効化し遷移完了まで覆い続ける 2026-06-30
    function pfShowCover() {
      var cover = document.querySelector('[data-pf-page-cover]');
      if (!cover) return;
      cover.style.animation = 'none';
      cover.style.opacity = '1';
      cover.style.visibility = 'visible';
      cover.removeAttribute('hidden');
    }
    function pfGoBack() {
      pfShowCover(); // 遷移前に背景シェルを隠す（遷移完了＝次ページ描画まで白で覆う）
      var ref = document.referrer || '';
      var sameOrigin = ref.indexOf(window.location.origin + '/') === 0;
      if (window.history.length > 1 && sameOrigin) {
        window.history.back();   // 直前ページ(通常はトップ)へ。bfcacheでスクロール位置も復元される
      } else {
        window.location.href = '/'; // 参照元が無い/別オリジンならトップへフォールバック
      }
    }
    function scheduleReturn() {
      if (__pfReturnTimer) clearTimeout(__pfReturnTimer);
      __pfReturnTimer = window.setTimeout(pfGoBack, 100);
    }
    function cancelReturn() {
      if (__pfReturnTimer) { clearTimeout(__pfReturnTimer); __pfReturnTimer = null; }
    }
    if (returnOnClose) {
      // 結果CTA「さらに見る」や商品リンク等の“前方遷移”時は戻り処理をスキップ
      container.addEventListener('click', function (e) {
        var a = e.target.closest('a[href]');
        if (a) {
          var href = a.getAttribute('href') || '';
          if (href && href.charAt(0) !== '#') __pfForwardNav = true;
        }
      }, true);
      // カート遷移等のJS離脱時は、予約済みの戻りをキャンセル（前方遷移を優先）
      window.addEventListener('pagehide', cancelReturn);
      window.addEventListener('beforeunload', cancelReturn);
    }

    if (modal) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.attributeName === 'open') {
            if (modal.hasAttribute('open')) {
              // Modal opened — check the pending category (set by click handler)
              var requestedCat = window.__pfPendingCategory || '';
              window.__pfPendingCategory = '';
              handleModalOpen(requestedCat);
            } else {
              window.setTimeout(reset, 50);
              // [カスタマイズ追記] 閉じた瞬間に白カバーで背景シェルを隠してから戻る（遷移ギャップで背景を見せない）2026-06-30
              if (returnOnClose && !__pfForwardNav) {
                pfShowCover();
                scheduleReturn();
              }
            }
            break;
          }
        }
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

      // [カスタマイズ追記] 診断ページ：?cat= を読んで自動オープン（cat無し→カテゴリピッカー）2026-06-29
      if (returnOnClose && !modal.hasAttribute('open')) {
        var urlCat = '';
        try { urlCat = new URLSearchParams(window.location.search).get('cat') || ''; } catch (e) {}
        window.__pfPendingCategory = urlCat || '__picker__';
        if (typeof modal.show === 'function') {
          modal.show();
        } else {
          modal.setAttribute('open', '');
        }
      } else if (modal.hasAttribute('open')) {
        handleModalOpen('');
      }
    }
  }

  /* --------------------------------------------------------------------------
     Alias modal redirector.

     Architecture:
       - One MAIN <flexible-modal id="product-navigator"> (contains all panels)
       - N ALIAS <flexible-modal id="product-navigator-{cat}"> (empty stubs)
       - Links like <a href="#product-navigator-haircare"> open the alias.
       - When an alias opens, we hide() it and show() the main modal with
         window.__pfPendingCategory set.

     This avoids flexible-modal.js's strict id parsing (no query params allowed).
     -------------------------------------------------------------------------- */
  function bindAliasModals() {
    if (window.__pfAliasModalsBound) return;
    window.__pfAliasModalsBound = true;

    var aliases = document.querySelectorAll('flexible-modal.product-navigator-alias');
    Array.prototype.forEach.call(aliases, function (alias) {
      if (alias.__pfAliasBound) return;
      alias.__pfAliasBound = true;

      var observer = new MutationObserver(function () {
        if (!alias.hasAttribute('open')) return;

        var targetId = alias.getAttribute('data-pf-redirect-to');
        var cat = alias.getAttribute('data-pf-cat-handle') || '';
        var mainModal = document.getElementById(targetId);
        if (!mainModal) return;

        // Stash category for main modal's open handler
        window.__pfPendingCategory = cat;

        // Close alias and open main modal
        if (typeof alias.hide === 'function') alias.hide();
        else alias.removeAttribute('open');

        window.setTimeout(function () {
          if (typeof mainModal.show === 'function') {
            mainModal.show();
          }
        }, 10);
      });
      observer.observe(alias, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* --------------------------------------------------------------------------
     Bootstrap
     -------------------------------------------------------------------------- */
  function init() {
    bindAliasModals();
    var containers = document.querySelectorAll('[data-pf-container]');
    Array.prototype.forEach.call(containers, initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();